import { useState } from 'react';
import { Card, CardContent, Button, Input } from '../ui';
import type { ExtractedId } from '../../lib/claudeVision';

interface DrugIdConfirmationProps {
  title: string;
  subtitle?: string;
  detectedIds: ExtractedId[];
  onConfirm: (ids: string[]) => void;
  onBack: () => void;
  image?: string | null;
  expectedCount?: number; // If known, show "X of Y selected"
}

export function DrugIdConfirmation({
  title,
  subtitle,
  detectedIds,
  onConfirm,
  onBack,
  image,
  expectedCount
}: DrugIdConfirmationProps) {
  // Initialize with all detected IDs selected
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(detectedIds.map(id => id.value))
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [manualId, setManualId] = useState('');

  // Confidence states are domain colors — hues kept, dark pairs added
  const confidenceColors = {
    high: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    low: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
  };

  const toggleId = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const startEditing = (id: string) => {
    setEditingId(id);
    setEditValue(id);
  };

  const saveEdit = () => {
    if (!editingId || !editValue.trim()) return;

    const newSelected = new Set(selectedIds);
    newSelected.delete(editingId);
    newSelected.add(editValue.trim().toUpperCase());
    setSelectedIds(newSelected);
    setEditingId(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const addManualId = () => {
    if (!manualId.trim()) return;
    const newSelected = new Set(selectedIds);
    newSelected.add(manualId.trim().toUpperCase());
    setSelectedIds(newSelected);
    setManualId('');
  };

  const removeId = (id: string) => {
    const newSelected = new Set(selectedIds);
    newSelected.delete(id);
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    setSelectedIds(new Set(detectedIds.map(id => id.value)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedIds));
  };

  // Get all IDs (detected + any manually added that aren't in detected)
  const detectedValues = new Set(detectedIds.map(id => id.value));
  const manuallyAddedIds = Array.from(selectedIds).filter(id => !detectedValues.has(id));

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-4">
          {image && (
            <div className="mb-4">
              <img
                src={image}
                alt="Captured document"
                className="w-full max-h-48 object-contain rounded-lg bg-gray-100 dark:bg-gray-900"
              />
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-100">{title}</h3>
                {subtitle && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
                )}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedIds.size} selected
                {expectedCount && ` of ${expectedCount} expected`}
              </div>
            </div>

            {/* Detected IDs with checkboxes */}
            {detectedIds.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Detected IDs:</span>
                  <div className="space-x-2">
                    <button
                      onClick={selectAll}
                      className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      Select all
                    </button>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <button
                      onClick={deselectAll}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      Deselect all
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  {detectedIds.map((id, index) => (
                    <div
                      key={`${id.value}-${index}`}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors duration-150 ${
                        selectedIds.has(id.value)
                          ? 'bg-primary-50 border-primary-300 dark:bg-primary-900/20 dark:border-primary-700'
                          : 'bg-gray-50 border-gray-200 dark:bg-gray-900/40 dark:border-gray-700'
                      }`}
                    >
                      {editingId === id.value ? (
                        <div className="flex-1 flex items-center gap-2">
                          <Input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="flex-1 font-mono"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEdit();
                              if (e.key === 'Escape') cancelEdit();
                            }}
                          />
                          <Button size="sm" onClick={saveEdit}>Save</Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(id.value)}
                            onChange={() => toggleId(id.value)}
                            className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                          />
                          <div className="flex-1">
                            <span className="font-mono text-lg text-gray-900 dark:text-gray-100">{id.value}</span>
                            {id.context && (
                              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">({id.context})</span>
                            )}
                          </div>
                          <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 text-xs font-medium rounded-full ${confidenceColors[id.confidence]}`}>
                            {id.confidence}
                          </span>
                          <button
                            onClick={() => startEditing(id.value)}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                            title="Edit ID"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Manually added IDs */}
            {manuallyAddedIds.length > 0 && (
              <div className="space-y-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Manually added:</span>
                <div className="space-y-2">
                  {manuallyAddedIds.map((id) => (
                    <div
                      key={id}
                      className="flex items-center gap-3 p-3 rounded-lg border bg-primary-50 border-primary-300 dark:bg-primary-900/20 dark:border-primary-700"
                    >
                      <input
                        type="checkbox"
                        checked={true}
                        onChange={() => removeId(id)}
                        className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                      />
                      <span className="flex-1 font-mono text-lg text-gray-900 dark:text-gray-100">{id}</span>
                      <span className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300">
                        manual
                      </span>
                      <button
                        onClick={() => removeId(id)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400"
                        title="Remove ID"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add manual ID */}
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Add ID manually
              </label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                  placeholder="Enter ID number"
                  className="flex-1 font-mono"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addManualId();
                  }}
                />
                <Button
                  onClick={addManualId}
                  disabled={!manualId.trim()}
                  variant="outline"
                >
                  Add
                </Button>
              </div>
            </div>

            {/* No IDs message */}
            {detectedIds.length === 0 && selectedIds.size === 0 && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                <p>No IDs detected. Please add IDs manually.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={selectedIds.size === 0}
          className="flex-1"
        >
          Confirm {selectedIds.size} ID{selectedIds.size !== 1 ? 's' : ''}
          <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>
      </div>
    </div>
  );
}
